/** @jsxImportSource @emotion/react */
import { Link } from 'react-router';

import type { ApiScan, ScanPartHistoryRecord } from '@kaiizen/kai-pro-api';

import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { getTotalCostIfDefined, type ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import {
  getApproverUserIdByScanId,
  getLocalDayKey,
  getScanActivitySections,
  getTaskEditName,
  getTaskEditRows,
  getTaskPartInfoByScanPartId,
  getTaskScopeNoteRows,
  getTaskTotalCostAt,
  getTaskWorkProgressDetail,
  getTaskWorkProgressName,
  getTaskWorkProgressRows,
  groupConsecutiveActivityLogEntries,
  hasModifierChange,
  isContractorAssignment,
  isTaskCreation,
  SCAN_APPROVAL_EVENT_TYPES,
  type ScanActivitySection,
  type TaskEditRow,
  type TaskScopeNoteRow,
  type TaskWorkProgressRow,
} from '@/Client/Lib';
import { formatUsd } from '@/Client/Presenters';
import {
  getScanUrl,
  useProjectActivityLog,
  useScanEditingContextSlice,
  useScanPartHistory,
  useScanPartNotes,
} from '@/Client/State';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { TaskStatusBadge } from '@/Client/Widgets';
import { CompleteIcon, FlagIcon, NotStartedIcon } from '@kaiizen/kai-shiki/react';
import { isHistoricalScopeArtifactScan } from '../../FocusPanel/FocusedArtifactPanel/FocusedArtifactPanelLib';
import {
  ACTIVITY_LOG_TABLE_MARK_SIZE,
  ActivityLogTable,
  type ActivityLogTableEntry,
  type ActivityLogTableSection,
  type ActivityLogTableStyles,
  markColumn,
  whatColumn,
  whenColumn,
  whoColumn,
} from '../ActivityLogTable';
import { TaskActivityChangesSummary } from './TaskActivityChangesSummary';
import { TaskActivityChangesTooltipTable } from './TaskActivityChangesTooltipTable';
import { DEFAULT_TASK_ACTIVITY_LOG_STYLES, TASK_ACTIVITY_LOG_BADGE_STYLES } from './taskActivityLogStyles';

const TASK_ACTIVITY_LOG_COLUMNS = [whenColumn, markColumn, whoColumn, whatColumn];

/** Enough to cover every approval on a project; approvals are only ever a handful. */
const SCAN_APPROVAL_EVENTS_LIMIT = 100;

const toWorkProgressEntry = (row: TaskWorkProgressRow): ActivityLogTableEntry => ({
  id: row.id,
  at: row.at,
  actorUserId: row.actorUserId,
  mark: <TaskStatusBadge status={row.status} iconOnly styles={TASK_ACTIVITY_LOG_BADGE_STYLES} />,
  name: getTaskWorkProgressName(row),
  detail: getTaskWorkProgressDetail(row),
  tone: row.status === 'needsRework' ? 'alert' : 'default',
});

/** Change-order work takes the red flag: a line item edit made once the scope was first approved, and any modifier added or removed. */
const isChangeOrderEdit = (row: TaskEditRow, changeOrdersStartAt: Date | undefined) =>
  hasModifierChange(row) || (row.partType === 'lineItem' && !!changeOrdersStartAt && row.at >= changeOrdersStartAt);

const changeOrderMark = <FlagIcon size={ACTIVITY_LOG_TABLE_MARK_SIZE} color={COLORS.error} data-testid={'task-activity-change-order-mark'} />;
const editMark = <span css={DEFAULT_TASK_ACTIVITY_LOG_STYLES.editMark} data-testid={'task-activity-edit-mark'} />;

const toEditEntry = (row: TaskEditRow, changeOrdersStartAt: Date | undefined): ActivityLogTableEntry => ({
  id: row.id,
  at: row.at,
  actorUserId: row.actorUserId,
  mark: isTaskCreation(row)
    ? <span css={DEFAULT_TASK_ACTIVITY_LOG_STYLES.outlineMark} data-testid={'task-activity-created-mark'} />
    : isChangeOrderEdit(row, changeOrdersStartAt)
    ? changeOrderMark
    : editMark,
  name: getTaskEditName(row),
  detail: isContractorAssignment(row)
    ? row.changes[0].after
    : row.kind === 'updated'
    ? <TaskActivityChangesSummary changes={row.visibleChanges} />
    : undefined,
  tooltip: row.changes.length > 0 ? <TaskActivityChangesTooltipTable changes={row.changes} /> : undefined,
  tooltipTitle: isTaskCreation(row) ? 'Created with' : row.kind === 'added' ? 'Added with' : 'Changes',
});

const toScopeNoteEntry = (row: TaskScopeNoteRow): ActivityLogTableEntry => ({
  id: row.id,
  at: row.at,
  actorUserId: row.actorUserId,
  mark: row.noteType === 'approval'
    ? <TaskStatusBadge status={'approved'} iconOnly styles={TASK_ACTIVITY_LOG_BADGE_STYLES} />
    : <span css={DEFAULT_TASK_ACTIVITY_LOG_STYLES.outlineMark} data-testid={'task-activity-note-mark'} />,
  name: row.name,
  detail: row.body,
});

/** An entry, and the run it may collapse into: consecutive edits sharing a run key group together. */
type TaskActivityItem = { entry: ActivityLogTableEntry; runKey?: string; isChangeOrder?: boolean };

const byNewestFirst = (a: TaskActivityItem, b: TaskActivityItem) => b.entry.at.getTime() - a.entry.at.getTime();

/** A run of edits as one collapsed row: "6 edits", flagged when any of them is change-order work. */
const toEditGroupItem = (run: TaskActivityItem[]): TaskActivityItem => ({
  entry: {
    id: `edits-${run[0].entry.id}`,
    at: run[0].entry.at,
    actorUserId: run[0].entry.actorUserId,
    mark: run.some(item => item.isChangeOrder) ? changeOrderMark : editMark,
    name: `${run.length} edits`,
    children: run.map(item => item.entry),
  },
});

/**
 * The task's total cost as of a scope version: as approved, for an approved version; live, for the
 * draft being viewed; otherwise as it stands now.
 */
const getSectionTotalCost = (section: ScanActivitySection, viewedScan: ApiScan, task: ScanTaskOptimized, scanPartHistory: readonly ScanPartHistoryRecord[]) =>
  !section.isApproved && section.scanId === viewedScan.id
    ? getTotalCostIfDefined(task)
    : getTaskTotalCostAt({ task, scanPartHistory, at: section.isApproved ? section.at : new Date() });

/**
 * A scope version's heading, linking to its artifact in the artifact panel: the change order, for a
 * change-order version (its updated scope is not linked), otherwise the scope. An approved version is
 * marked with the solid green diamond; a draft with the empty black one, and its label says so
 * ("Draft Artifact").
 */
const toSectionHeading = (section: ScanActivitySection, viewedScan: ApiScan, totalCost: number | undefined): ActivityLogTableSection => {
  const getArtifactUrl = (artifactDocument: 'fullScope' | 'changeOrder') =>
    getScanUrl({ scanId: viewedScan.id, focusPanel: 'artifact', artifactScanId: section.scanId, artifactDocument });
  return {
    id: section.id,
    startsAt: section.startsAt,
    at: section.at,
    actorUserId: section.actorUserId,
    aside: totalCost === undefined ? undefined : formatUsd(totalCost, false, true),
    mark: section.isApproved
      ? <CompleteIcon size={ACTIVITY_LOG_TABLE_MARK_SIZE} color={COLORS.success} data-testid={'task-activity-approved-scan-mark'} />
      : <NotStartedIcon size={ACTIVITY_LOG_TABLE_MARK_SIZE} color={COLORS.ink} data-testid={'task-activity-draft-scan-mark'} />,
    title: (
      <>
        {`${section.isApproved ? '' : 'Draft '}Artifact:\u00A0 `}
        {section.changeOrderNumber === undefined
          ? <Link css={DEFAULT_TASK_ACTIVITY_LOG_STYLES.sectionLink} to={getArtifactUrl('fullScope')}>{`Scope of Work v${section.scopeVersion}`}</Link>
          : <Link css={DEFAULT_TASK_ACTIVITY_LOG_STYLES.sectionLink} to={getArtifactUrl('changeOrder')}>{`Change Order ${section.changeOrderNumber}`}</Link>}
      </>
    ),
  };
};

type TaskActivityLogProps = { task: ScanTaskOptimized; styles?: ActivityLogTableStyles };

/**
 * A task's activity log: its work-status history, read from the task's own progress updates, with
 * each rework's Instruction Note as its reason; its scope-approval notes (edit requests, their
 * resolutions and approvals, and approvals); and every saved edit to the task, its options and their
 * line items, each with a tooltip of the fields it changed (change-order work marked with the red
 * flag: line item edits from the first change order on, and any modifier added or removed), read
 * from the task's scan-data history.
 *
 * Each run of edits (adds, updates and removals) by one person on one day, in the viewer's time zone,
 * collapses into a single "N edits" row, its caret showing the edits beneath it.
 *
 * Events sit under a heading per scope version ("Scope of Work v1", then "Change Order 1", …),
 * linking to that document: each version's heading gathers the events from its
 * scan's creation until the next version's, shows when it was approved and by whom and the task's
 * total cost as of then, and goes unshown when no event falls under it.
 */
const FullTaskActivityLog = ({ task, styles }: TaskActivityLogProps) => {
  const { scan, scans, scanNotes, contractorsMap, knownTaskModifiers } = useScanEditingContextSlice([
    'scan',
    'scans',
    'scanNotes',
    'contractorsMap',
    'knownTaskModifiers',
  ]);
  const { entries: approvalEvents } = useProjectActivityLog(scan?.propertyProjectId, {
    limit: SCAN_APPROVAL_EVENTS_LIMIT,
    eventTypes: SCAN_APPROVAL_EVENT_TYPES,
  });
  const { scanPartNotes } = useScanPartNotes(task.id);
  const { scanPartHistory } = useScanPartHistory(scan, task.id);

  const scanSections = scans?.length
    ? getScanActivitySections({
      scopeVersionScans: scans.filter(isHistoricalScopeArtifactScan),
      approverUserIdByScanId: getApproverUserIdByScanId(approvalEvents),
    })
    : [];
  // the second scope version is the first change order: line item edits from then on are change-order work
  const changeOrdersStartAt = scanSections[1]?.startsAt;

  // a run of edits stays within one scope version's section
  const getSectionStart = (at: Date) => scanSections.findLast(section => section.startsAt <= at)?.startsAt.getTime() ?? 0;
  const getEditRunKey = (row: TaskEditRow) => isTaskCreation(row) ? undefined : `${row.actorUserId ?? ''}|${getLocalDayKey(row.at)}|${getSectionStart(row.at)}`;

  const items: TaskActivityItem[] = [
    ...getTaskWorkProgressRows({ progressUpdates: task.progressUpdates, scanNotes: scanPartNotes }).map(row => ({ entry: toWorkProgressEntry(row) })),
    ...getTaskScopeNoteRows({ scanNotes, taskScanPartId: task.id }).map(row => ({ entry: toScopeNoteEntry(row) })),
    ...getTaskEditRows({
      scanPartHistory,
      partInfoByScanPartId: getTaskPartInfoByScanPartId(task),
      contractorNamesById: new Map(Object.values(contractorsMap ?? {}).map(contractor => [contractor.id, contractor.name])),
      knownModifiers: knownTaskModifiers,
    }).map(row => ({ entry: toEditEntry(row, changeOrdersStartAt), runKey: getEditRunKey(row), isChangeOrder: isChangeOrderEdit(row, changeOrdersStartAt) })),
  ].sort(byNewestFirst);
  const entries = groupConsecutiveActivityLogEntries(items, item => item.runKey, toEditGroupItem).map(item => item.entry);

  return (
    <div>
      <ActivityLogTable
        title={'Task activity'}
        emptyLabel={'No activity yet'}
        entries={entries}
        sections={scan && scanSections.length
          ? scanSections.map(section => toSectionHeading(section, scan, getSectionTotalCost(section, scan, task, scanPartHistory)))
          : undefined}
        columns={TASK_ACTIVITY_LOG_COLUMNS}
        styles={styles}
      />
    </div>
  );
};

/** The log before full edit history: the task's work-status history only. */
const WorkTaskActivityLog = ({ task, styles }: TaskActivityLogProps) => {
  const { scanPartNotes } = useScanPartNotes(task.id);
  const entries = getTaskWorkProgressRows({ progressUpdates: task.progressUpdates, scanNotes: scanPartNotes }).map(toWorkProgressEntry);
  return <ActivityLogTable title={'Task activity'} emptyLabel={'No activity yet'} entries={entries} columns={TASK_ACTIVITY_LOG_COLUMNS} styles={styles} />;
};

/** The full log is behind the `taskActivity` feature flag; without it, the work-status history alone. */
export const TaskActivityLog = (props: TaskActivityLogProps) => {
  const { featureFlags } = useFeatureFlags();
  return featureFlags.taskActivity ? <FullTaskActivityLog {...props} /> : <WorkTaskActivityLog {...props} />;
};
